import type { GeneralEventsMessage } from '../stores/types/chat';
import { getAuthenticatedRequestInit } from './utils';
import { ChatRoomStatus } from '../stores/types/types';
import type { MessagesSeenResult } from '../stores/types/connection';
import blpAuth from './blp-auth';
import { getEmbedSafeHeaders } from './auth';
import { getApiHost } from '../utils/hosts';

export const LoadDirectChat = (
	token: string,
	meeting: number,
	recipient?: number,
	lastId?: number,
) => {
	const queryParams = new URLSearchParams();

	if (recipient != null) queryParams.append('recipient', recipient.toString());
	if (lastId != null) queryParams.append('lastId', lastId.toString());

	const queryString = queryParams.toString();
	const url = `${getApiHost()}/v2/greenroom/chat/direct/${meeting}${queryString ? `?${queryString}` : ''}`;

	return fetch(
		url,
		{
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		},
	).then((result) => {
		if (result.ok) {
			return result.json();
		}
		return false;
	});
};

export const LoadChatRooms = (
	token: string,
	meeting: number,
	roomId?: number,
	page?: number,
	pageSize?: number,
) => {
	const queryParams = new URLSearchParams();
	if (roomId != null) queryParams.append('roomId', roomId.toString());
	if (page != null) queryParams.append('page', page.toString());
	if (pageSize != null) queryParams.append('pageSize', pageSize.toString());

	const queryString = queryParams.toString();
	const url = `${getApiHost()}/v2/greenroom/chat/rooms/${meeting}${queryString ? `?${queryString}` : ''}`;
	return fetch(
		url,
		{
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		},
	).then((result) => {
		if (result.ok) {
			return result.json();
		}
		return [];
	});
};

export const LoadRoomMessages = (
	token: string,
	meeting: number,
	roomId?: number,
	lastId?: number,
) => {
	const queryParams = new URLSearchParams();
	if (roomId != null) queryParams.append('roomId', roomId.toString());
	if (lastId != null) queryParams.append('lastId', lastId.toString());

	const queryString = queryParams.toString();
	const url = `${getApiHost()}/v2/greenroom/chat/messages/${meeting}${queryString ? `?${queryString}` : ''}`;
	return fetch(
		url,
		{
			method: 'GET',
			...getAuthenticatedRequestInit(token),
		},
	).then((result) => {
		if (result.ok) {
			return result.json();
		}
		return [];
	});
};

interface PostChatResult {
	meetingId: number;
	roomId: number;
	insertId: number;
}

export const PostChat = (
	token: string,
	meeting: number,
	message: string,
	attachment?: string,
	room?: number,
	recipient?: number,
): Promise<PostChatResult> => fetch(`${getApiHost()}/v2/greenroom/chat`, {
	method: 'POST',
	body: JSON.stringify({
		meeting,
		message,
		room,
		recipient,
		attachment,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return false;
});

export const CreateRoom = (token: string, meeting: number, name: string, participants: number[]) => fetch(`${getApiHost()}/v2/greenroom/chat/create`, {
	method: 'POST',
	body: JSON.stringify({
		meeting,
		name,
		participants,
	}),
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return false;
});

export const DeleteRoom = (
	channel: number,
	roomId: number
): Promise<number> => blpAuth.delete({
	url: `/v3/greenroom/chat/${channel}/delete/${roomId}`,
	host: getApiHost(),
	token: blpAuth.accessToken,
	options: { headers: getEmbedSafeHeaders() }
}).catch((e) => {
	console.error(e);
	return -1;
}).then((res) => res);

export const MessagesSeen = (
	token: string,
	meeting: number,
	authorOrRoomId: number,
	chatRoomStatus: ChatRoomStatus,
	messages: number[],
): Promise<MessagesSeenResult | boolean> => fetch(
	`${
		getApiHost()
	}/v2/greenroom/chat/direct/${meeting}/seen?messages=${messages.join(',')}`,
	{
		method: 'PUT',
		body: JSON.stringify({
			authorOrRoomId,
			chatRoomStatus,
		}),
		...getAuthenticatedRequestInit(token),
	},
).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return false;
});

// eslint-disable-next-line camelcase
export const GetModeratedComments = (token: string, live_chat: number) => fetch(
	// eslint-disable-next-line camelcase
	`${getApiHost()}/v2/greenroom/chat/moderated/${live_chat}`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return false;
});

// eslint-disable-next-line camelcase
export const GetApprovedComment = (token: string, chat_message: number) => fetch(
	// eslint-disable-next-line camelcase
	`${getApiHost()}/v2/greenroom/chat/moderated/single/${chat_message}`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return false;
});

export const GetPresenterMessages = (
	token: string,
	// eslint-disable-next-line camelcase
	meeting_id: string,
): Promise<GeneralEventsMessage[]> => fetch(
	// eslint-disable-next-line camelcase
	`${getApiHost()}/v2/greenroom/events/get-presenter-messages/${meeting_id}`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((result) => {
	if (result.ok) {
		return result.json();
	}
	return [];
});

export const UpdateModeratedEventsMessagesSeen = (
	token: string,
	// eslint-disable-next-line camelcase
	meeting_id: string,
	chatMessages: number[],
	questions: number[],
	comments: number[],
) => fetch(
	// eslint-disable-next-line camelcase
	`${getApiHost()}/v2/greenroom/events/update-presenter-messages/${meeting_id}/seen`,
	{
		method: 'PUT',
		body: JSON.stringify({
			chatMessages,
			questions,
			comments,
		}),
		...getAuthenticatedRequestInit(token),
	},
).then((result) => result.json());

export const SoftDeleteChatMessagesForMeetingAndRoom = async (
	token: string,
	meetingId: number,
	roomId: number,
	userId: number,
) => {
	try {
		const url = `${getApiHost()}/v2/greenroom/chat/${meetingId}/${roomId}/${userId}`;
		const response = await fetch(url, {
			method: 'DELETE',
			...getAuthenticatedRequestInit(token),
		});

		if (response.ok) {
			return response.json();
		}
	} catch (e) {
		console.error(e);
	}

	return false;
};

export const SoftDeleteChatMessageById = async (
	token: string,
	meetingId: number,
	messageId: number,
) => {
	try {
		const url = `${getApiHost()}/v2/greenroom/chat/${meetingId}/${messageId}`;
		const response = await fetch(url, {
			method: 'DELETE',
			...getAuthenticatedRequestInit(token),
		});

		if (response.ok) {
			return true;
		}
	} catch (e) {
		console.error(e);
	}

	return false;
};
